// LIBRARY
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Row, Col, Input, message } from 'antd';

// CUSTOM
import { UserAccessContext } from 'context/userAccess';
import TablePager from 'shared_components/table_pager';
import SearchPager from 'shared_components/search_pager';
import PageTitle from 'shared_components/page_title';
import DownloadCSV from 'shared_components/download_csv';
import { showMessage } from 'services/settings/message';
import {
	fetchSections,
	fetchSpecimens,
	fetchExamitems,
	applySorting,
} from 'services/settings/examItem';
// eslint-disable-next-line import/no-extraneous-dependencies
import { PlusOutlined } from '@ant-design/icons';
import ExamTable from './table';
import AddForm from './add_form';
import UpdateForm from './update_form';
import DropDown from '../shared/dropdown';
import { moduleTitle, tablePageSize, messagePrompts, buttonNames } from './settings';
import { GLOBAL_TABLE_PAGE_SIZE } from 'global_config/constant-global';

const { Search } = Input;

class ExamItems extends React.Component {
	state = {
		isInitializing: true,
		isLoading: false,
		isShowAddForm: false,
		isShowUpdateForm: false,
		pageSize: GLOBAL_TABLE_PAGE_SIZE,
		currentPage: 1,
		examItemsRef: [],
		examItems: [],
		ddSections: [],
		ddSpecimens: [],
		selectedSectionId: null,
		selectedSpecimenId: null,
		selectedExamItemId: null,
		selectedSectionName: null,
		selectedSpecimenName: null,
		searchTerm: '',
	};

	async componentDidMount() {
		setTimeout(async () => {
			const sections = await fetchSections();
			const activeSections = (sections || []).filter(item => item.isActive === 1);
			const sortedSections = activeSections.sort((a, b) => (a.sectionSort || 0) - (b.sectionSort || 0));

			const specimens = await fetchSpecimens();
			const activeSpecimens = (specimens || []).filter(item => item.isActive === 1);
			const sortedSpecimens = activeSpecimens.sort((a, b) => (a.specimenSort || 0) - (b.specimenSort || 0));

			const ddSections = sortedSections.map(section => ({
				label: section.sectionName,
				value: section.sectionID,
			}));

			const ddSpecimens = sortedSpecimens.map(specimen => ({
				label: specimen.specimenName.toUpperCase(),
				value: specimen.specimenID,
			}));

			let selectedSpecimenId = null;
			let selectedSpecimenName = null;

			if (sortedSpecimens.length > 0) {
				selectedSpecimenId = sortedSpecimens[0].specimenID ? sortedSpecimens[0].specimenID : null;
				selectedSpecimenName = sortedSpecimens[0].specimenName ? sortedSpecimens[0].specimenName : null;
			}

			this.setState({
				ddSections,
				ddSpecimens,
				selectedSpecimenId,
				selectedSpecimenName,
				isInitializing: false,
			});
		}, 500);
	}

	sortByExamItemSort = (examItems) => {
		return examItems.sort((a, b) => {
			const sortDiff = (a.examItemSort || 0) - (b.examItemSort || 0);
			if (sortDiff !== 0) return sortDiff;
			return (a.examItemID || 0) - (b.examItemID || 0);
		});
	};

	onClickAdd = () => {
		this.setState({ isShowAddForm: true });
	};

	onChangePager = pageSize => {
		this.setState({ pageSize, currentPage: 1 });
	};

	onPageChange = (page) => {
		this.setState({ currentPage: page });
		console.log('onPageChange page =>', page);
	};

	onChangeSectionCode = (sectionId, option) => {
		this.setState(
			{
				isLoading: true,
				selectedExamItemId: null,
				selectedSectionId: sectionId,
				currentPage: 1,
			},
			async () => {
				const { selectedSpecimenId: specimenID } = this.state;
				const fetchedExamItems = await fetchExamitems(sectionId, specimenID);
				const examItems = this.sortByExamItemSort(fetchedExamItems);

				this.setState({
					examItems,
					examItemsRef: examItems,
					isLoading: false,
					selectedSectionName: option.props.children,
				});
			},
		);
	};

	onChangeSpecimen = (specimenID, option) => {
		this.setState(
			{
				isLoading: true,
				selectedExamItemId: null,
				selectedSpecimenId: specimenID,
				currentPage: 1,
			},
			async () => {
				const { selectedSectionId: sectionId } = this.state;
				const fetchedExamItems = await fetchExamitems(sectionId, specimenID);
				const examItems = this.sortByExamItemSort(fetchedExamItems);

				console.log('onChangeSpecimen => examItems => ', examItems);

				this.setState({
					examItems,
					examItemsRef: examItems,
					isLoading: false,
					selectedSpecimenName: option.props.children,
				});
			},
		);
	};

	onSearch = value => {
		const { examItemsRef } = this.state;
		const searchedVal = value.toLowerCase();

		const filtered = examItemsRef.filter(item => {
			const { examItemName, examItemGeneralName, examItemIntegrationCode } = item;

			return (
				this.containsString(examItemName, searchedVal) ||
				this.containsString(examItemGeneralName, searchedVal) ||
				this.containsString(examItemIntegrationCode, searchedVal)
			);
		});

		// Show "No result found" message if no results
		if (filtered.length === 0 && value.trim() !== '') {
			showMessage('M0001');
		}

		this.setState({ examItems: filtered, currentPage: 1, searchTerm: value });
	};

	onChangeSearch = event => {
		const { examItemsRef, selectedSectionId } = this.state;
		this.setState({ searchTerm: event.target.value });

		// Only reset if section is selected and value is empty
		if (event.target.value === '' && selectedSectionId !== null) {
			this.setState({ examItems: examItemsRef });
		}
	};

	// Private Function
	containsString = (searchFrom, searchedVal) => {
		if (searchFrom === null || searchFrom === undefined || searchFrom === '') return false;

		return searchFrom
			.toString()
			.toLowerCase()
			.includes(searchedVal);
	};

	onDblClickTableRow = selectedExamItem => {
		this.setState({
			isShowUpdateForm: true,
			selectedExamItemId: selectedExamItem.examItemID,
		});
	};

	onExitForm = () => {
		this.setState({ isShowAddForm: false, isShowUpdateForm: false });
	};

	onSuccessCreateExamItem = () => {
		this.setState({ isLoading: true, isShowAddForm: false }, async () => {
			const { selectedSectionId: sectionId, selectedSpecimenId: specimenId, pageSize } = this.state;
			const fetchedExamItems = await fetchExamitems(sectionId, specimenId);
			const examItems = this.sortByExamItemSort(fetchedExamItems);
			
			const nextPage = Math.ceil(examItems.length / pageSize);
			
			this.setState({ examItems, examItemsRef: examItems, isLoading: false, currentPage: nextPage });
		});

		showMessage('M0002');
	};

	onSuccessUpdateExamItem = () => {
		this.setState({ isLoading: true, isShowUpdateForm: false }, async () => {
			const { selectedSectionId: sectionId, selectedSpecimenId: specimenId } = this.state;
			const fetchedExamItems = await fetchExamitems(sectionId, specimenId);
			const examItems = this.sortByExamItemSort(fetchedExamItems);
			console.log('onSuccessUpdateExamItem => examItems => ', examItems);
			this.setState({ examItems, examItemsRef: examItems, isLoading: false });
		});

		showMessage('M0003');
	};

	applySort = async () => {
		const { examItems, selectedSectionId, selectedSpecimenId } = this.state;
		const dataPayload = examItems.map((rec, index) => {
			return {
				sectionID: selectedSectionId,
				specimenID: selectedSpecimenId,
				examItemID: rec.examItemID,
				examItemSort: index + 1,
			};
		});

		const sortResponse = await applySorting(dataPayload);
		if (sortResponse) {
			showMessage('M0006');

			// Refetch and sort the data
			this.setState({ isLoading: true }, async () => {
				const fetchedExamItems = await fetchExamitems(selectedSectionId, selectedSpecimenId);
				const sortedExamItems = this.sortByExamItemSort(fetchedExamItems);

				this.setState({
					examItems: sortedExamItems,
					examItemsRef: sortedExamItems,
					isLoading: false
				});
			});
		}
	};

	sortExamItems = newExamItems => {
		this.setState({ examItems: newExamItems });
	};

	render() {
		const {
			pageSize,
			currentPage,
			examItems,
			examItemsRef,
			ddSpecimens,
			ddSections,
			isInitializing,
			isShowAddForm,
			isShowUpdateForm,
			isLoading,
			selectedSpecimenId,
			selectedSectionId,
			selectedExamItemId,
			selectedSectionName,
			selectedSpecimenName,
		} = this.state;

		return (
			<div>

				<PageTitle pageTitle={moduleTitle} />


				<Row
					style={{
						display: 'flex',
						gap: '10px',
						justifyContent: 'space-between',
						alignItems: 'end',
						marginTop: 30,
					}}
				>
					<Col style={{ display: 'flex', textAlign: 'left', gap: '10px' }}>
						<div>
							<p style={{ margin: 0 }}>SECTION</p>
							<DropDown
								placeholder="Select Section"
								content={ddSections}
								onChange={this.onChangeSectionCode}
								loading={isInitializing}
								value={selectedSectionId}
							/>
						</div>
						<div>
							<p style={{ margin: 0 }}>SPECIMEN</p>
							<DropDown
								size="small"
								placeholder="Filter by Specimen"
								disabled={selectedSectionId == null}
								content={ddSpecimens}
								onChange={this.onChangeSpecimen}
								loading={isInitializing}
								value={selectedSpecimenId}
							/>
						</div>
						<div>
							<p style={{ margin: 0 }}>SEARCH</p>
							<Search
								allowClear
								onSearch={value => this.onSearch(value)}
								onChange={this.onChangeSearch}
								style={{ width: 200 }}
								disabled={selectedSectionId == null}
								className="exam-item-search-input"
							/>
						</div>
					</Col>
					<Col style={{ display: 'flex', gap: '10px', justifyContent: 'end' }}>
						<DownloadCSV
							disabled={selectedSectionId === null}
							confirmRequired={true}
							confirmDetails={{
								section: selectedSectionName,
								specimen: selectedSpecimenName,
								count: (examItemsRef || []).length,
							}}
							data={examItemsRef}
							filename="exam_results"
							columns={[
								{ title: 'EXAM RESULT ID', dataIndex: 'examItemID' },
								{ title: 'EXAM RESULT NAME', dataIndex: 'examItemName' },
								{ title: 'ALTERNATIVE NAME', dataIndex: 'examItemGeneralName' },
								{ 
									title: 'EXAM RESULT TYPE', 
									dataIndex: 'examItemTypeCode',
								},
								{ title: 'INTEGRATION CODE', dataIndex: 'examItemIntegrationCode' },
								{ 
									title: 'STATUS', 
									dataIndex: 'active',
									render: (val) => val === 1 ? 'ACTIVE' : 'INACTIVE'
								}
							]}
						/>
						<Button onClick={this.applySort} shape="round" type="primary">
							APPLY SORTING
						</Button>
						<UserAccessContext.Consumer>
							{value =>
								value.userAccess.settings.create && (
									<Button
										shape="round"
										type="primary"
										onClick={this.onClickAdd}
										disabled={selectedSectionId === null}
									>
										<PlusOutlined /> {buttonNames.addExamItem}
									</Button>
								)
							}
						</UserAccessContext.Consumer>
						{/* <TablePager handleChange={this.onChangePager} /> */}
					</Col>
				</Row>
				<Row>
					<Col span={24}>
						<SearchPager
							pageSize={pageSize}
							pageTotal={examItems.length}
							currentPage={currentPage}
							handleChangeSize={this.onChangePager}
						/>
					</Col>
				</Row>

				<ExamTable
					data={examItems}
					pageSize={pageSize}
					currentPage={currentPage}
					loading={isLoading}
					onRowDblClick={this.onDblClickTableRow}
					onDragAndDropRow={this.sortExamItems}
					onPageChange={this.onPageChange}
				/>

				<AddForm
					visible={isShowAddForm}
					onClose={this.onExitForm}
					onSuccess={this.onSuccessCreateExamItem}
					selectedSectionId={selectedSectionId}
					selectedSpecimenId={selectedSpecimenId}
					selectedSectionName={selectedSectionName}
					selectedSpecimenName={selectedSpecimenName}
					selectedExamItemList={examItems}
				/>
				<UpdateForm
					visible={isShowUpdateForm}
					onClose={this.onExitForm}
					onSuccess={this.onSuccessUpdateExamItem}
					selectedSectionId={selectedSectionId}
					selectedSpecimenId={selectedSpecimenId}
					selectedExamItemId={selectedExamItemId}
					selectedSectionName={selectedSectionName}
					selectedSpecimenName={selectedSpecimenName}
					selectedExamItemList={examItems}
				/>
			</div>
		);
	}
}

export default ExamItems;
